import { useTranslation } from "react-i18next";

import { PermissionRoles } from "@/enum";
import { ROUTE_CONFIG } from "@/routes/config";
import { useIsFeatureActive } from "@/utils/useIsFeatureActive";
import { useHasPermissions } from "@/utils/useHasPermissions";

import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";
import { useGetCopywritingFrameworkReviewStatusQuery } from "@/pages/Private/redux/copywritingFramework/copywritingFramework.api";
import { useGetKnowledgeDbReviewStatusQuery } from "@/pages/Private/redux/knowledgeDb/knowledgeDb.api";
import { useGetTemplatesReviewStatusQuery } from "@/pages/Private/redux/messageTemplate/messageTemplate.api";

export interface SectionReview {
	/** Whether the section still has AI-generated / unreviewed content. */
	pending: boolean;
	/** Tooltip shown on the orange nav dot. */
	tooltip: React.ReactNode;
}

export type AiReviewStatus = Record<string, SectionReview>;

export interface ReviewBubble {
	bubbleColor: "orange";
	bubbleText: React.ReactNode;
}

/**
 * Resolves the orange "needs review" nav dot for a given route. The Knowledge DB
 * dot only appears once Lead Scoring is green (PREC-3631); the other sections are
 * ungated. Pure so it can be unit-tested without rendering the Sidebar.
 */
export function resolveReviewBubble(
	path: string | undefined,
	aiReviewStatus: AiReviewStatus,
	isLeadScoringGreen: boolean
): ReviewBubble | undefined {
	const review = path ? aiReviewStatus[path] : undefined;

	if (!review?.pending) {
		return undefined;
	}

	if (path === ROUTE_CONFIG.KNOWLEDGE_DB && !isLeadScoringGreen) {
		return undefined;
	}

	return { bubbleColor: "orange", bubbleText: review.tooltip };
}

/**
 * Derives the per-section "needs human review" state that drives the orange
 * left-nav dot (PREC-3631) for Knowledge DB, Copywriting and Templates.
 *
 * The Knowledge DB dot is additionally gated on Lead Scoring being green — that
 * gating lives in the Sidebar, which already knows the lead-scoring status.
 */
export const useAiReviewStatus = (): AiReviewStatus => {
	const { t } = useTranslation();
	const { hasPermissions } = useHasPermissions();

	// Each review-status query must only fire when the user is actually entitled
	// to that section — otherwise the request 403s and crashes the dashboard for
	// users without the feature/permission. We mirror the exact gating the
	// Sidebar uses to decide whether the nav item is shown (sidenav.config.tsx).
	const canSeeKnowledgeDb = useIsFeatureActive(FeaturesCodes.KNOWLEDGE_DB) === true;
	const canSeeCopywriting =
		useIsFeatureActive(FeaturesCodes.COPYWRITING_SETTINGS) === true &&
		hasPermissions([PermissionRoles.COPYWRITING_SETTINGS]);
	const canSeeTemplates = useIsFeatureActive(FeaturesCodes.TEMPLATES) === true;

	const { data: knowledgeDb } = useGetKnowledgeDbReviewStatusQuery(undefined, {
		skip: !canSeeKnowledgeDb,
	});
	const { data: copywriting } = useGetCopywritingFrameworkReviewStatusQuery(undefined, {
		skip: !canSeeCopywriting,
	});
	const { data: templates } = useGetTemplatesReviewStatusQuery(undefined, {
		skip: !canSeeTemplates,
	});

	return {
		[ROUTE_CONFIG.KNOWLEDGE_DB]: {
			pending: !!knowledgeDb?.hasPendingReview,
			tooltip: t("sidebar.reviewDot.knowledgeDb"),
		},
		[ROUTE_CONFIG.COPYWRITING]: {
			pending: !!copywriting?.hasPendingReview,
			tooltip: t("sidebar.reviewDot.copywriting"),
		},
		[ROUTE_CONFIG.TEMPLATES]: {
			pending: !!templates?.hasPendingReview,
			tooltip: t("sidebar.reviewDot.templates"),
		},
	};
};
